import { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import { useSvgToolHandoffReceiver } from "../../hooks/useSvgToolHandoffReceiver";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { isValidSvg, getSvgDimensions } from "../../utils/svgUtils";

interface UploadedSvg {
    name: string;
    content: string;
}

interface SvgToRasterState {
    svg: UploadedSvg | null;
    width: number;
    height: number;
    transparency: boolean;
    antiAliasing: boolean;
    maxColors: number;
    binaryAlpha: boolean;
    alphaThreshold: number;
    autoGenerate: boolean;
    rasterImageDataUrl: string | null;
    error: string | null;
    setSvg: (svg: UploadedSvg | null) => void;
    setWidth: (width: number) => void;
    setHeight: (height: number) => void;
    setTransparency: (transparency: boolean) => void;
    setAntiAliasing: (antiAliasing: boolean) => void;
    setMaxColors: (maxColors: number) => void;
    setBinaryAlpha: (binaryAlpha: boolean) => void;
    setAlphaThreshold: (alphaThreshold: number) => void;
    setAutoGenerate: (autoGenerate: boolean) => void;
    setError: (error: string | null) => void;
    generateRasterImage: () => Promise<void>;
    canExport: boolean;
    handleDownload: () => void;
    svgInputRef: React.RefObject<HTMLInputElement>;
    handleFileLoad: (event: React.ChangeEvent<HTMLInputElement>) => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const SvgToRasterContext = createContext<SvgToRasterState | null>(null);

export function useSvgToRaster() {
    const context = useContext(SvgToRasterContext);
    if (!context) {
        throw new Error(
            "useSvgToRaster must be used within SvgToRasterProvider"
        );
    }
    return context;
}

interface SvgToRasterProviderProps {
    children: React.ReactNode;
    initialWidth?: number;
    initialHeight?: number;
    initialTransparency?: boolean;
    initialAntiAliasing?: boolean;
    initialMaxColors?: number;
    initialBinaryAlpha?: boolean;
    initialAlphaThreshold?: number;
}

export function SvgToRasterProvider({
    children,
    initialWidth = 64,
    initialHeight = 64,
    initialTransparency = true,
    initialAntiAliasing = false,
    initialMaxColors = 256,
    initialBinaryAlpha = false,
    initialAlphaThreshold = 128,
}: SvgToRasterProviderProps) {
    const [svg, setSvg] = useState<UploadedSvg | null>(null);
    const [width, setWidth] = useState(initialWidth);
    const [height, setHeight] = useState(initialHeight);
    const [transparency, setTransparency] = useState(initialTransparency);
    const [antiAliasing, setAntiAliasing] = useState(initialAntiAliasing);
    const [maxColors, setMaxColors] = useState(initialMaxColors);
    const [binaryAlpha, setBinaryAlpha] = useState(initialBinaryAlpha);
    const [alphaThreshold, setAlphaThreshold] = useState(initialAlphaThreshold);
    const [autoGenerate, setAutoGenerate] = useState(false);
    const [rasterImageDataUrl, setRasterImageDataUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [aspectRatio, setAspectRatio] = useState<number | null>(null);
    const svgInputRef = useRef<HTMLInputElement | null>(null);

    const ingestHandoffSvg = useCallback(
        (svg: UploadedSvg) => {
            setError(null);
            const text = svg.content;
            const dimensions = getSvgDimensions(text);
            let newAspectRatio: number | null = null;
            let calculatedWidth = initialWidth;
            let calculatedHeight = initialHeight;

            if (dimensions && dimensions.width > 0 && dimensions.height > 0) {
                newAspectRatio = dimensions.width / dimensions.height;
                calculatedWidth = 64;
                calculatedHeight = Math.round(64 / newAspectRatio);
            }

            setSvg(svg);
            setAspectRatio(newAspectRatio);
            setWidth(calculatedWidth);
            setHeight(calculatedHeight);
            setRasterImageDataUrl(null);
        },
        [initialWidth, initialHeight],
    );

    const handleFileLoad = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>): void => {
            const file = event.target.files?.[0];
            if (!file) return;

            setError(null);

            if (
                !file.type.includes("svg") &&
                !file.name.toLowerCase().endsWith(".svg")
            ) {
                setError("Please choose an SVG file.");
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                const text =
                    typeof reader.result === "string" ? reader.result : "";

                if (!text) {
                    setError("Unable to read SVG file.");
                    return;
                }

                if (!isValidSvg(text)) {
                    setError("Selected file does not appear to be valid SVG.");
                    return;
                }

                ingestHandoffSvg({ name: file.name, content: text });
            };

            reader.onerror = () => {
                setError("Error reading file.");
            };

            reader.readAsText(file);
        },
        [ingestHandoffSvg],
    );

    // Color quantization function using simple palette reduction
    const quantizeColors = useCallback((imageData: ImageData, maxColors: number): ImageData => {
        const data = new Uint8ClampedArray(imageData.data);
        const colorMap = new Map<string, { r: number; g: number; b: number; a: number; count: number }>();
        
        // Collect all unique colors with their counts
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];
            
            // Skip fully transparent pixels
            if (a === 0) continue;
            
            const key = `${r},${g},${b},${a}`;
            if (colorMap.has(key)) {
                colorMap.get(key)!.count++;
            } else {
                colorMap.set(key, { r, g, b, a, count: 1 });
            }
        }
        
        // If we already have fewer colors than max, no need to quantize
        if (colorMap.size <= maxColors) {
            return imageData;
        }
        
        // Build palette using k-means-like approach (simplified)
        // Sort colors by frequency and take top colors
        const sortedColors = Array.from(colorMap.entries())
            .sort((a, b) => b[1].count - a[1].count)
            .slice(0, maxColors)
            .map(([_, color]) => color);
        
        // Create a color lookup map for fast nearest color finding
        const palette = sortedColors;
        
        // Quantize all pixels to nearest palette color
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];
            
            // Keep fully transparent pixels as-is
            if (a === 0) continue;
            
            // Find nearest color in palette
            let minDist = Infinity;
            let nearestColor = palette[0];
            
            for (const color of palette) {
                const dr = r - color.r;
                const dg = g - color.g;
                const db = b - color.b;
                const dist = dr * dr + dg * dg + db * db;
                
                if (dist < minDist) {
                    minDist = dist;
                    nearestColor = color;
                }
            }
            
            data[i] = nearestColor.r;
            data[i + 1] = nearestColor.g;
            data[i + 2] = nearestColor.b;
            // Keep original alpha
        }
        
        return new ImageData(data, imageData.width, imageData.height);
    }, []);

    const generateRasterImage = useCallback(async (): Promise<void> => {
        if (!svg || width <= 0 || height <= 0) {
            setError("Invalid parameters: SVG must be loaded and dimensions must be positive.");
            return;
        }

        setError(null);

        try {
            // Create a blob from SVG content
            const svgBlob = new Blob([svg.content], {
                type: "image/svg+xml;charset=utf-8",
            });
            const svgUrl = URL.createObjectURL(svgBlob);

            // Create an image element to load the SVG
            const img = new Image();
            
            await new Promise<void>((resolve, reject) => {
                img.onload = () => resolve();
                img.onerror = () => reject(new Error("Failed to load SVG image"));
                img.src = svgUrl;
            });

            // Create canvas
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");

            if (!ctx) {
                throw new Error("Failed to get canvas context");
            }

            // Set image smoothing based on anti-aliasing setting
            ctx.imageSmoothingEnabled = antiAliasing;

            // Clear canvas with transparent or white background
            if (transparency) {
                ctx.clearRect(0, 0, width, height);
            } else {
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, width, height);
            }

            // Draw SVG to canvas
            ctx.drawImage(img, 0, 0, width, height);

            // Get image data for processing
            const imageData = ctx.getImageData(0, 0, width, height);

            // Apply binary alpha (0% or 100% only) if enabled
            if (binaryAlpha) {
                const data = imageData.data;
                for (let i = 3; i < data.length; i += 4) {
                    // Convert alpha based on threshold: above threshold = 255 (opaque), below = 0 (transparent)
                    if (data[i] >= alphaThreshold) {
                        data[i] = 255;
                    } else {
                        data[i] = 0;
                    }
                }
            }

            // Apply color quantization if maxColors is set and less than 256
            if (maxColors > 0 && maxColors < 256) {
                const quantizedData = quantizeColors(imageData, maxColors);
                ctx.putImageData(quantizedData, 0, 0);
            } else if (binaryAlpha) {
                // If only binary alpha was applied, put the data back
                ctx.putImageData(imageData, 0, 0);
            }

            // Convert to data URL
            const dataUrl = transparency
                ? canvas.toDataURL("image/png") // PNG supports transparency
                : canvas.toDataURL("image/png"); // Still use PNG for quality

            setRasterImageDataUrl(dataUrl);
            URL.revokeObjectURL(svgUrl);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to generate raster image";
            setError(errorMessage);
            setRasterImageDataUrl(null);
        }
    }, [svg, width, height, transparency, antiAliasing, maxColors, binaryAlpha, alphaThreshold, quantizeColors]);

    // Auto-generate when parameters change if autoGenerate is enabled
    useEffect(() => {
        if (autoGenerate && svg && width > 0 && height > 0) {
            generateRasterImage();
        }
    }, [autoGenerate, svg, width, height, transparency, antiAliasing, maxColors, binaryAlpha, alphaThreshold, generateRasterImage]);

    const canExport = !!rasterImageDataUrl;

    // Auto-update height when width changes (if aspect ratio is known)
    const handleSetWidth = useCallback((newWidth: number) => {
        setWidth(newWidth);
        if (aspectRatio !== null && aspectRatio > 0) {
            setHeight(Math.round(newWidth / aspectRatio));
        }
    }, [aspectRatio]);

    const handleDownload = useCallback(() => {
        if (!rasterImageDataUrl || !svg) return;

        const link = document.createElement("a");
        link.href = rasterImageDataUrl;
        const baseName = svg.name.replace(/\.svg$/i, "");
        link.download = `${baseName}-${width}x${height}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [rasterImageDataUrl, svg, width, height]);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            width, height, transparency, antiAliasing, maxColors,
            binaryAlpha, alphaThreshold,
        }),
        [width, height, transparency, antiAliasing, maxColors, binaryAlpha, alphaThreshold],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.width != null) setWidth(values.width as number);
        if (values.height != null) setHeight(values.height as number);
        if (values.transparency != null) setTransparency(values.transparency as boolean);
        if (values.antiAliasing != null) setAntiAliasing(values.antiAliasing as boolean);
        if (values.maxColors != null) setMaxColors(values.maxColors as number);
        if (values.binaryAlpha != null) setBinaryAlpha(values.binaryAlpha as boolean);
        if (values.alphaThreshold != null) setAlphaThreshold(values.alphaThreshold as number);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "svg-to-raster",
        defaultPreset: {
            width: initialWidth,
            height: initialHeight,
            transparency: initialTransparency,
            antiAliasing: initialAntiAliasing,
            maxColors: initialMaxColors,
            binaryAlpha: initialBinaryAlpha,
            alphaThreshold: initialAlphaThreshold,
        },
        getPresetValues,
        applyPreset,
    });

    useSvgToolHandoffReceiver(
        "svg-to-raster",
        { onPrimary: (svg) => ingestHandoffSvg(svg) },
        { setError },
    );

    const value: SvgToRasterState = {
        svg,
        width,
        height,
        transparency,
        antiAliasing,
        maxColors,
        binaryAlpha,
        alphaThreshold,
        autoGenerate,
        rasterImageDataUrl,
        error,
        setSvg,
        setWidth: handleSetWidth,
        setHeight,
        setTransparency,
        setAntiAliasing,
        setMaxColors,
        setBinaryAlpha,
        setAlphaThreshold,
        setAutoGenerate,
        setError,
        generateRasterImage,
        canExport,
        handleDownload,
        svgInputRef,
        handleFileLoad,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <SvgToRasterContext.Provider value={value}>
            {children}
        </SvgToRasterContext.Provider>
    );
}
